import React, { useState, useEffect, useRef } from "react";
import { useDispatch } from "react-redux";
import { CButton, CForm, CAlert } from "@coreui/react";
import { useNavigate } from "react-router-dom";
import openEyeIcon from "../../assets/Images/eye-open.png";
import closedEyeIcon from "../../assets/Images/eye-closed.png";
import logoImg from "../../assets/Images/White_Logo.svg";
import * as actions from "../../store/actions/index";
import { SOCKET_BASE_URL } from "../Helpers/helpers";

// ─── OTP / Registration constants ────────────────────────────────────────────
const OTP_LENGTH = 6;
const OTP_EXPIRY_SECONDS = 5 * 60; // 5 minutes
const RESEND_COOLDOWN_SECONDS = 30;
const MAX_OTP_ATTEMPTS = 5;

// Registration steps
const STEP_FORM = "FORM";
const STEP_OTP = "OTP";
const STEP_DONE = "DONE";

// ─── API helpers ─────────────────────────────────────────────────────────────
const apiRequest = async (path, body) => {
    let response;
    try {
        response = await fetch(`${SOCKET_BASE_URL}${path}`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(body),
        });
    } catch (networkErr) {
        throw new ApiError(
            "Network error. Please check your connection and try again.",
            0,
            networkErr,
        );
    }

    let data = null;
    try {
        data = await response.json();
    } catch {
    }

    if (!response.ok) {
        const message =
            data?.message ||
            data?.error ||
            defaultMessageForStatus(response.status);
        throw new ApiError(message, response.status, data);
    }

    return data;
};

class ApiError extends Error {
    constructor(message, status, payload) {
        super(message);
        this.name = "ApiError";
        this.status = status;
        this.payload = payload;
    }
}

const defaultMessageForStatus = (status) => {
    switch (status) {
        case 400:
            return "Invalid request. Please check the details you entered.";
        case 401:
            return "Session expired. Please try again.";
        case 409:
            return "An account with this email already exists.";
        case 422:
            return "The information provided is invalid.";
        case 429:
            return "Too many attempts. Please wait before trying again.";
        case 500:
        case 502:
        case 503:
            return "Server error. Please try again in a moment.";
        default:
            return "Something went wrong. Please try again.";
    }
};

const registerApi = {
  register: (payload) =>
    apiRequest("/api/v1/user/register", payload),

  verifyOtp: (payload) =>
    apiRequest(
      "/api/v1/user/auth/verify-email-otp",
      payload
    ),

  resendOtp: (payload) =>
    apiRequest(
      "/api/v1/user/auth/resend-email-otp",
      payload
    ),
};

// ─── Validation helpers ──────────────────────────────────────────────────────
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
// Success criteria: at least 8 characters, 1 uppercase, 1 number, 1 special character.
const PASSWORD_RE = /^(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z0-9]).{8,}$/;
const PASSWORD_EXAMPLE = "Secur3!";

const validateForm = ({ firstName, lastName, email, password, confirmPassword }) => {
    const errors = {};
    if (!firstName || firstName.trim().length < 2) {
        errors.firstName = "Please enter your first name.";
    }
    if (!lastName || lastName.trim().length < 2) {
        errors.lastName = "Please enter your last name.";
    }
    if (!email || !EMAIL_RE.test(email)) {
        errors.email = "Please enter a valid email address.";
    }
    if (!password || !PASSWORD_RE.test(password)) {
        errors.password = "Min 8 chars, 1 uppercase, 1 number, 1 special character.";
    }
    if (confirmPassword !== password) {
        errors.confirmPassword = "Passwords do not match.";
    }
    return errors;
};

// ─── Password strength ───────────────────────────────────────────────────────
// Returns null for an empty password, otherwise a label/percent/color for the meter.
const getPasswordStrength = (pwd) => {
    if (!pwd) return null;

    if (pwd.length < 8) {
        return { label: "Too short", percent: 15, color: "#e55353" };
    }

    let score = 0;
    if (pwd.length >= 8) score++;
    if (/[a-z]/.test(pwd)) score++;
    if (/[A-Z]/.test(pwd)) score++;
    if (/\d/.test(pwd)) score++;
    if (/[^A-Za-z0-9]/.test(pwd)) score++;

    if (score <= 2) return { label: "Weak", percent: 32, color: "#e55353" };
    if (score === 3) return { label: "Fair", percent: 56, color: "#f0a020" };
    if (score === 4) return { label: "Good", percent: 78, color: "#2f78c4" };
    return { label: "Strong", percent: 100, color: "#22b573" };
};

// ─── Component ────────────────────────────────────────────────────────────────
const RegisterNew = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const [step, setStep] = useState(STEP_FORM);

    // Browser tab title for this page.
    useEffect(() => {
        document.title = "Create an Account - QuickQuery";
    }, []);

    // Form fields
    const [firstName, setFirstName] = useState("");
    const [lastName, setLastName] = useState("");
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [passwordVisible, setPasswordVisible] = useState(false);
    const [confirmVisible, setConfirmVisible] = useState(false);

    const [fieldErrors, setFieldErrors] = useState({});
    const [validated, setValidated] = useState(false);

    // Alert / feedback state
    const [showError, setShowError] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");
    const [showSuccess, setShowSuccess] = useState(false);
    const [successMessage, setSuccessMessage] = useState("");

    const [loading, setLoading] = useState(false);

    // OTP state (sent to email)
    const [otp, setOtp] = useState(Array(OTP_LENGTH).fill(""));
    const [otpExpiresAt, setOtpExpiresAt] = useState(null);
    const [otpRemainingSeconds, setOtpRemainingSeconds] = useState(0);
    const [otpAttemptsLeft, setOtpAttemptsLeft] = useState(MAX_OTP_ATTEMPTS);
    const [resendCooldown, setResendCooldown] = useState(0);
    const [resendLoading, setResendLoading] = useState(false);
    const otpInputRefs = useRef([]);

    // ── OTP expiry countdown ──────────────────────────────────────────────────
    useEffect(() => {
        if (step !== STEP_OTP || !otpExpiresAt) return;

        const tick = () => {
            const secs = Math.max(0, Math.ceil((otpExpiresAt - Date.now()) / 1000));
            setOtpRemainingSeconds(secs);
            if (secs <= 0) {
                setShowError(true);
                setErrorMessage("Your OTP has expired. Please request a new code.");
            }
        };

        tick();
        const interval = setInterval(tick, 1000);
        return () => clearInterval(interval);
    }, [step, otpExpiresAt]);

    // ── Resend cooldown countdown ─────────────────────────────────────────────
    useEffect(() => {
        if (resendCooldown <= 0) return;
        const interval = setInterval(() => {
            setResendCooldown((s) => Math.max(0, s - 1));
        }, 1000);
        return () => clearInterval(interval);
    }, [resendCooldown]);

    const formatCountdown = (secs) => {
        const m = Math.floor(secs / 60).toString().padStart(2, "0");
        const s = (secs % 60).toString().padStart(2, "0");
        return `${m}:${s}`;
    };

    // ── Field change handlers ─────────────────────────────────────────────────
    const clearFieldError = (field) => {
        setFieldErrors((prev) => {
            if (!prev[field]) return prev;
            const next = { ...prev };
            delete next[field];
            return next;
        });
        setShowError(false);
    };

    const handleFirstNameChange = (e) => {
        setFirstName(e.target.value);
        clearFieldError("firstName");
    };
    const handleLastNameChange = (e) => {
        setLastName(e.target.value);
        clearFieldError("lastName");
    };
    const handleEmailChange = (e) => {
        setEmail(e.target.value);
        clearFieldError("email");
    };
    const handlePasswordChange = (e) => {
        setPassword(e.target.value);
        clearFieldError("password");
        // Re-check the match as soon as both fields have values.
        if (confirmPassword) clearFieldError("confirmPassword");
    };
    const handleConfirmPasswordChange = (e) => {
        setConfirmPassword(e.target.value);
        clearFieldError("confirmPassword");
    };

    const handleCancel = () => {
        setFirstName("");
        setLastName("");
        setEmail("");
        setPassword("");
        setConfirmPassword("");
        setFieldErrors({});
        setValidated(false);
        setShowError(false);
        setShowSuccess(false);
    };

    // All fields must have a value before the submit button is enabled.
    const allFieldsFilled =
        firstName.trim() !== "" &&
        lastName.trim() !== "" &&
        email.trim() !== "" &&
        password !== "" &&
        confirmPassword !== "";

    const passwordStrength = getPasswordStrength(password);
    const isPasswordValid = PASSWORD_RE.test(password);

    // ── Submit registration form ──────────────────────────────────────────────
    const handleRegisterSubmit = async (event) => {
        event.preventDefault();

        const errors = validateForm({
            firstName,
            lastName,
            email,
            password,
            confirmPassword,
        });

        setFieldErrors(errors);
        setValidated(true);

        if (Object.keys(errors).length > 0) {
            return;
        }

        setLoading(true);
        setShowError(false);
        setErrorMessage("");

        try {
            const payload = {
                FirstName: firstName.trim(),
                LastName: lastName.trim(),
                EmailAddress: email.trim().toLowerCase(),
                Password: password,
                ConfirmPassword: confirmPassword,
            };

            const response = await registerApi.register(payload);

            if (response && response.success === true) {
                const registeredEmail = payload.EmailAddress;

                navigate(
                    `/verify-email?email=${encodeURIComponent(registeredEmail)}`,
                    { replace: true },
                );

                setStep(STEP_OTP);
                setOtpExpiresAt(Date.now() + OTP_EXPIRY_SECONDS * 1000);
                return;
            }

            setShowError(true);
            setErrorMessage(
                response?.message || "Registration failed. Please try again."
            );
        } catch (err) {
            setShowError(true);

            if (err instanceof ApiError) {
                if (err.status === 409) {
                    setFieldErrors({ email: "This email is already registered." });
                }
                setErrorMessage(err.message);
            } else {
                setErrorMessage("An unexpected error occurred. Please try again.");
            }
        } finally {
            setLoading(false);
        }
    };

    // ── OTP input handlers ────────────────────────────────────────────────────
    const handleOtpChange = (index, value) => {
        const digit = value.replace(/[^0-9]/g, "").slice(-1);
        const next = [...otp];
        next[index] = digit;
        setOtp(next);
        setShowError(false);

        if (digit && index < OTP_LENGTH - 1) {
            otpInputRefs.current[index + 1]?.focus();
        }
    };

    const handleOtpKeyDown = (index, e) => {
        if (e.key === "Backspace" && !otp[index] && index > 0) {
            otpInputRefs.current[index - 1]?.focus();
        }
    };

    const handleOtpPaste = (e) => {
        const pasted = e.clipboardData.getData("text").replace(/[^0-9]/g, "");
        if (!pasted) return;
        e.preventDefault();
        const next = Array(OTP_LENGTH).fill("");
        pasted
            .slice(0, OTP_LENGTH)
            .split("")
            .forEach((d, i) => (next[i] = d));
        setOtp(next);
        const lastIndex = Math.min(pasted.length, OTP_LENGTH) - 1;
        otpInputRefs.current[lastIndex]?.focus();
    };

    // ── Verify OTP (email code) ──────────────────────────────────────────────
    const handleVerifyOtp = async (event) => {
        event.preventDefault();

        if (otpRemainingSeconds <= 0) {
            setShowError(true);
            setErrorMessage("Your OTP has expired. Please request a new code.");
            return;
        }

        const code = otp.join("");
        if (code.length !== OTP_LENGTH) {
            setShowError(true);
            setErrorMessage(`Please enter the ${OTP_LENGTH}-digit code.`);
            return;
        }

        setLoading(true);
        setShowError(false);

        try {
            await registerApi.verifyOtp({
                email: email.trim().toLowerCase(),
                code,
            });

            try {
                await dispatch(actions.userLogin(email.trim().toLowerCase(), password));
            } catch {
                // Non-fatal: verification succeeded even if auto-login didn't.
            }

            setStep(STEP_DONE);
            setShowSuccess(true);
            setSuccessMessage("Your account has been verified successfully!");
            setTimeout(() => navigate("/query"), 1200);
        } catch (err) {
            setShowError(true);
            if (err instanceof ApiError) {
                if (err.status === 410) {
                    setOtpRemainingSeconds(0);
                    setErrorMessage("Your OTP has expired. Please request a new code.");
                } else {
                    const attemptsLeft = Math.max(0, otpAttemptsLeft - 1);
                    setOtpAttemptsLeft(attemptsLeft);
                    setErrorMessage(
                        attemptsLeft > 0
                            ? `${err.message} (${attemptsLeft} attempt${attemptsLeft !== 1 ? "s" : ""} left)`
                            : "Too many incorrect attempts. Please request a new code.",
                    );
                }
            } else {
                setErrorMessage("An unexpected error occurred. Please try again.");
            }
        } finally {
            setLoading(false);
        }
    };

    // ── Resend OTP (email code) ──────────────────────────────────────────────
    const handleResendOtp = async () => {
        if (resendCooldown > 0 || resendLoading) return;

        setResendLoading(true);
        setShowError(false);

        try {
            const data = await registerApi.resendOtp({
                email: email.trim().toLowerCase(),
            });

            const expiresInSeconds = data?.expiresIn ?? OTP_EXPIRY_SECONDS;
            setOtpExpiresAt(Date.now() + expiresInSeconds * 1000);
            setOtpAttemptsLeft(MAX_OTP_ATTEMPTS);
            setOtp(Array(OTP_LENGTH).fill(""));
            setResendCooldown(RESEND_COOLDOWN_SECONDS);
            setShowSuccess(true);
            setSuccessMessage("A new verification code has been emailed to you.");
            otpInputRefs.current[0]?.focus();
        } catch (err) {
            setShowError(true);
            setErrorMessage(
                err instanceof ApiError
                    ? err.message
                    : "Could not resend the code. Please try again.",
            );
        } finally {
            setResendLoading(false);
        }
    };

    const goBackToForm = () => {
        setStep(STEP_FORM);
        setShowError(false);
        setShowSuccess(false);
    };

    const eyeIcon = (visible) => (visible ? openEyeIcon : closedEyeIcon);

    return (
        <div className="qq-login-page">
            {/* ───────── Left: brand / pitch panel ───────── */}
            <div className="qq-left">
                <div className="qq-left-inner">
                    <div className="qq-brand">
                        <img src={logoImg} alt="QuickQuery" className="qq-logo" />
                    </div>

                    {/* <div className="qq-version-pill">
                        <span className="qq-version-dot" />
                        Version 1 Active
                    </div> */}

                    <h1 className="qq-headline">Automate your account mapping in minutes</h1>
                    <p className="qq-subtext">
                        Configure standard expense categories, run clean multi-factor
                        compliance reporting, and synchronize seamlessly to your general
                        ledger.
                    </p>

                    {/* <div className="qq-card">
                        <div className="qq-card-head">
                            <span className="qq-card-head-left">Active Integrations</span>
                            <span className="qq-online-badge">ONLINE</span>
                        </div>
                        <div className="qq-card-stats">
                            <div>
                                <div className="qq-card-stat-label">MAPPED ACCOUNTS</div>
                                <div className="qq-card-stat-value">14,204</div>
                            </div>
                            <div>
                                <div className="qq-card-stat-label">SYNC ACCURACY</div>
                                <div className="qq-card-stat-value qq-green">99.98%</div>
                            </div>
                        </div>
                    </div> */}
                </div>

                {/* <div className="qq-footer-stats">
                    <div>
                        <div className="qq-footer-stat-value">10k+</div>
                        <div className="qq-footer-stat-label">HAPPY ENTERPRISE TEAMS</div>
                    </div>
                    <div>
                        <div className="qq-footer-stat-value">$2.4B+</div>
                        <div className="qq-footer-stat-label">MAPPED TRANSACTIONS</div>
                    </div>
                    <div>
                        <div className="qq-footer-stat-value">12 mins</div>
                        <div className="qq-footer-stat-label">AVERAGE SETUP TIME</div>
                    </div>
                </div> */}
            </div>

            {/* ───────── Right: form panel ───────── */}
            <div className="qq-right">
                <div className="qq-auth-card">
                    {step === STEP_FORM && (
                        <>
                            <h2 className="qq-auth-title">Create your account</h2>
                            <p className="qq-auth-subtitle">
                                You will verify your email next, then create your first
                                workspace.
                            </p>

                            {showError && (
                                <CAlert color="warning" dismissible onClose={() => setShowError(false)} className="mt-3 mb-0">
                                    {errorMessage}
                                </CAlert>
                            )}
                            {showSuccess && (
                                <CAlert color="success" dismissible onClose={() => setShowSuccess(false)} className="mt-3 mb-0">
                                    {successMessage}
                                </CAlert>
                            )}

                            <CForm noValidate validated={validated} onSubmit={handleRegisterSubmit}>
                                {/* First / Last name */}
                                <div className="qq-field-row">
                                    <div className="qq-field">
                                        <label className="qq-field-label" htmlFor="FirstName">First Name</label>
                                        <input
                                            id="FirstName"
                                            type="text"
                                            className={`qq-plain-input${fieldErrors.firstName ? " qq-invalid" : ""}`}
                                            placeholder="e.g. Sarah"
                                            value={firstName}
                                            onChange={handleFirstNameChange}
                                        />
                                        {fieldErrors.firstName && <div className="qq-field-error">{fieldErrors.firstName}</div>}
                                    </div>
                                    <div className="qq-field">
                                        <label className="qq-field-label" htmlFor="LastName">Last Name</label>
                                        <input
                                            id="LastName"
                                            type="text"
                                            className={`qq-plain-input${fieldErrors.lastName ? " qq-invalid" : ""}`}
                                            placeholder="e.g. Connor"
                                            value={lastName}
                                            onChange={handleLastNameChange}
                                        />
                                        {fieldErrors.lastName && <div className="qq-field-error">{fieldErrors.lastName}</div>}
                                    </div>
                                </div>

                                {/* Email */}
                                <div className="qq-field">
                                    <label className="qq-field-label" htmlFor="EmailAddress">Email Address</label>
                                    <input
                                        id="EmailAddress"
                                        type="email"
                                        className={`qq-plain-input${fieldErrors.email ? " qq-invalid" : ""}`}
                                        placeholder="you@company.com"
                                        value={email}
                                        onChange={handleEmailChange}
                                    />
                                    {fieldErrors.email && <div className="qq-field-error">{fieldErrors.email}</div>}
                                </div>

                                {/* Password */}
                                <div className="qq-field">
                                    <label className="qq-field-label" htmlFor="Password">Password</label>
                                    <div className="qq-password-wrap">
                                        <input
                                            id="Password"
                                            type={passwordVisible ? "text" : "password"}
                                            className={`qq-plain-input${fieldErrors.password ? " qq-invalid" : ""}`}
                                            placeholder="At least 8 characters"
                                            value={password}
                                            onChange={handlePasswordChange}
                                        />
                                        {isPasswordValid && (
                                            <span className="qq-check-badge" aria-label="Password meets requirements">
                                                ✓
                                            </span>
                                        )}
                                        <img
                                            src={eyeIcon(passwordVisible)}
                                            alt=""
                                            className="qq-eye-btn"
                                            onClick={() => setPasswordVisible((v) => !v)}
                                        />
                                    </div>

                                    {/* Short suggestion / validation note — only takes a line when it's actually needed. */}
                                    {fieldErrors.password ? (
                                        <div className="qq-field-error">{fieldErrors.password}</div>
                                    ) : (
                                        <div className="qq-field-hint">
                                            <strong>Success criteria:</strong> 8+ chars, 1 uppercase, 1 number, 1 special char
                                            (e.g. <strong>{PASSWORD_EXAMPLE}</strong>).
                                        </div>
                                    )}

                                    {passwordStrength && (
                                        <div className="qq-strength">
                                            <div className="qq-strength-row">
                                                <span className="qq-strength-label">Password strength</span>
                                                <span
                                                    className="qq-strength-value"
                                                    style={{ color: passwordStrength.color }}
                                                >
                                                    {passwordStrength.label}
                                                </span>
                                            </div>
                                            <div className="qq-strength-track">
                                                <div
                                                    className="qq-strength-fill"
                                                    style={{
                                                        width: `${passwordStrength.percent}%`,
                                                        background: passwordStrength.color,
                                                    }}
                                                />
                                            </div>
                                        </div>
                                    )}
                                </div>

                                {/* Retype Password */}
                                <div className="qq-field">
                                    <label className="qq-field-label" htmlFor="ConfirmPassword">Retype Password</label>
                                    <div className="qq-password-wrap">
                                        <input
                                            id="ConfirmPassword"
                                            type={confirmVisible ? "text" : "password"}
                                            className={`qq-plain-input${fieldErrors.confirmPassword ? " qq-invalid" : ""}`}
                                            placeholder="Re-enter your password"
                                            value={confirmPassword}
                                            onChange={handleConfirmPasswordChange}
                                        />
                                        {confirmPassword && confirmPassword === password && (
                                            <span className="qq-check-badge" aria-label="Passwords match">
                                                ✓
                                            </span>
                                        )}
                                        <img
                                            src={eyeIcon(confirmVisible)}
                                            alt=""
                                            className="qq-eye-btn"
                                            onClick={() => setConfirmVisible((v) => !v)}
                                        />
                                    </div>
                                    {fieldErrors.confirmPassword && (
                                        <div className="qq-field-error">{fieldErrors.confirmPassword}</div>
                                    )}
                                </div>

                                <CButton
                                    type="submit"
                                    className="qq-submit-btn"
                                    disabled={loading || !allFieldsFilled}
                                >
                                    {loading ? "Please wait..." : "Create account"}
                                </CButton>

                                {/* <div className="qq-fields-note">
                                    All fields are required before the account can be created.
                                </div> */}

                                <CButton type="button" className="qq-cancel-btn" onClick={handleCancel} disabled={loading}>
                                    Cancel
                                </CButton>
                            </CForm>

                            <div className="qq-signup-row">
                                Already have an account?{" "}
                                <span className="qq-signup-link" onClick={() => navigate("/login")}>
                                    Sign in
                                </span>
                            </div>
                        </>
                    )}

                    {step === STEP_OTP && (
                        <>
                            <h2 className="qq-auth-title">Verify your email</h2>
                            <p className="qq-auth-subtitle">
                                Enter the {OTP_LENGTH}-digit code emailed to <strong>{email}</strong>
                            </p>

                            {showError && (
                                <CAlert color="warning" dismissible onClose={() => setShowError(false)} className="mt-3 mb-0">
                                    {errorMessage}
                                </CAlert>
                            )}
                            {showSuccess && (
                                <CAlert color="success" dismissible onClose={() => setShowSuccess(false)} className="mt-3 mb-0">
                                    {successMessage}
                                </CAlert>
                            )}

                            <CForm noValidate onSubmit={handleVerifyOtp}>
                                <div className="qq-otp-row" onPaste={handleOtpPaste}>
                                    {otp.map((digit, i) => (
                                        <input
                                            key={i}
                                            ref={(el) => (otpInputRefs.current[i] = el)}
                                            value={digit}
                                            onChange={(e) => handleOtpChange(i, e.target.value)}
                                            onKeyDown={(e) => handleOtpKeyDown(i, e)}
                                            inputMode="numeric"
                                            maxLength={1}
                                            disabled={otpRemainingSeconds <= 0}
                                            className="qq-otp-box"
                                        />
                                    ))}
                                </div>

                                <div className="qq-otp-meta">
                                    {otpRemainingSeconds > 0 ? (
                                        <span>Code expires in <strong>{formatCountdown(otpRemainingSeconds)}</strong></span>
                                    ) : (
                                        <span style={{ color: "#e55353", fontWeight: 600 }}>Code expired</span>
                                    )}
                                </div>

                                <CButton
                                    type="submit"
                                    className="qq-submit-btn"
                                    disabled={loading || otpRemainingSeconds <= 0}
                                >
                                    {loading ? "Verifying..." : "Verify"}
                                </CButton>

                                <div className="qq-otp-meta">
                                    Didn't get the code?{" "}
                                    {resendCooldown > 0 ? (
                                        <span className="qq-otp-muted">Resend in {formatCountdown(resendCooldown)}</span>
                                    ) : (
                                        <span className="qq-otp-link" onClick={handleResendOtp}>
                                            {resendLoading ? "Sending..." : "Resend code"}
                                        </span>
                                    )}
                                </div>

                                <div className="qq-otp-meta">
                                    Wrong details?{" "}
                                    <span className="qq-otp-link" onClick={goBackToForm}>Back to details</span>
                                </div>
                            </CForm>
                        </>
                    )}

                    {step === STEP_DONE && (
                        <div className="text-center">
                            <h2 className="qq-auth-title">You're all set!</h2>
                            <CAlert color="success" className="mt-3">{successMessage}</CAlert>
                            <p className="qq-auth-subtitle">Redirecting…</p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default RegisterNew;